<template>
  <v-app>
    <v-container class="pb-10">
      <div class="decks-v2-container">
        <!-- Cabeçalho -->
        <div class="mb-6">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3">
            <div>
              <div class="d-flex align-center">
                <v-icon color="#315fd8" class="mr-2" size="32">mdi-cards</v-icon>
                <h1 class="text-h4 font-weight-bold" style="color: #0f172a;">Deck Flashcards 2.0</h1>
              </div>
              <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
                Novo padrão pedagógico baseado nas provas TEMFC • 1.752 cards categorizados com repetição espaçada SM-2
              </p>
            </div>

            <!-- Resumo Rápido SM-2 -->
            <div class="header-badges-container d-flex align-center flex-wrap">
              <v-chip color="primary" variant="flat" class="font-weight-bold header-badge mr-2">
                <v-icon start size="16">mdi-cards-outline</v-icon>
                {{ totalCardsCount }} Cards Ativos
              </v-chip>
              <v-btn
                v-if="dueCardsCount > 0"
                color="error"
                variant="elevated"
                class="font-weight-bold text-none header-due-btn"
                elevation="1"
                @click="studyDueCards"
              >
                <v-icon start size="16">mdi-clock-alert-outline</v-icon>
                {{ dueCardsCount }} para revisar hoje • Iniciar
              </v-btn>
              <v-chip v-else color="success" variant="tonal" class="font-weight-bold header-badge">
                <v-icon start size="16">mdi-check-circle-outline</v-icon>
                Revisões em dia!
              </v-chip>
            </div>
          </div>

          <!-- Tabs de Bloco -->
          <v-tabs v-model="activeBlockTab" class="mt-6 vs-tabs" color="primary" align-tabs="start">
            <v-tab value="conceitos" class="font-weight-bold text-none">
              <v-icon start>mdi-brain</v-icon>
              Conceitos Importantes ({{ conceitosTotalCards }})
            </v-tab>
            <v-tab value="clinicos" class="font-weight-bold text-none">
              <v-icon start>mdi-stethoscope</v-icon>
              Temas Clínicos ({{ clinicosTotalCards }})
            </v-tab>
          </v-tabs>
          <v-divider></v-divider>
        </div>

        <!-- Conteúdo do Bloco 1: Conceitos Importantes -->
        <div v-show="activeBlockTab === 'conceitos'">
          <v-alert
            color="primary"
            variant="tonal"
            class="mb-4 rounded-lg text-body-2"
            icon="mdi-information-outline"
          >
            {{ conceitosBlock?.descricao || 'Fundamentos da APS/MFC, SUS, saúde coletiva, comunicação e prevenção.' }}
          </v-alert>

          <!-- Lista de Temas de Conceitos -->
          <v-expansion-panels variant="accordion" class="vs-panels">
            <v-expansion-panel
              v-for="tema in conceitosTemas"
              :key="tema.deck_id"
              class="mb-2 rounded-lg elevation-1"
            >
              <v-expansion-panel-title class="py-3">
                <div class="d-flex align-center justify-space-between w-100 pr-2">
                  <div class="d-flex align-center">
                    <span class="font-weight-bold text-subtitle-1" style="color: #1e293b;">
                      {{ tema.rotulo }}
                    </span>
                  </div>
                  <div class="d-flex align-center" @click.stop>
                    <v-chip size="x-small" color="primary" variant="outlined" class="mr-2 font-weight-bold">
                      {{ tema.n_cards }} cards
                    </v-chip>
                    <v-chip v-if="tema.peso_prova" size="x-small" color="amber-darken-3" variant="tonal" class="mr-3 font-weight-bold">
                      {{ tema.peso_prova }} questões
                    </v-chip>
                    <v-btn
                      icon
                      size="small"
                      color="primary"
                      elevation="1"
                      class="play-btn"
                      :disabled="isDemoRestricted(tema)"
                      @click.stop="startStudy(tema)"
                      title="Estudar este tema"
                    >
                      <v-icon size="18">mdi-play</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-list density="compact" class="py-0">
                  <v-list-item
                    v-for="subtema in tema.subtemas"
                    :key="subtema.deck_id"
                    class="py-2 px-3 mb-1 subtema-item rounded"
                  >
                    <template #prepend>
                      <v-icon size="16" color="#315fd8" class="mr-2">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 font-weight-medium text-wrap" style="color: #334155;">
                      {{ subtema.rotulo }}
                    </v-list-item-title>
                    <template #append>
                      <div class="d-flex align-center">
                        <v-chip size="x-small" color="grey-darken-1" variant="tonal" class="mr-2">
                          {{ subtema.n_cards }} cards
                        </v-chip>
                        <v-chip v-if="subtema.peso_prova" size="x-small" color="amber-darken-3" variant="outlined" class="mr-3">
                          {{ subtema.peso_prova }} q
                        </v-chip>
                        <v-btn
                          icon
                          size="x-small"
                          color="primary"
                          variant="elevated"
                          class="play-sub-btn"
                          :disabled="isDemoRestricted(subtema)"
                          @click.stop="startStudy(subtema)"
                          title="Estudar este subtema"
                        >
                          <v-icon size="14">mdi-play</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <!-- Conteúdo do Bloco 2: Temas Clínicos (com 4 Ciclos) -->
        <div v-show="activeBlockTab === 'clinicos'">
          <!-- Subtabs para os 4 Ciclos de Vida -->
          <v-tabs v-model="activeCicloTab" class="mb-4 ciclo-tabs" color="primary" align-tabs="start">
            <v-tab
              v-for="ciclo in clinicosCiclos"
              :key="ciclo.deck_id"
              :value="ciclo.deck_id"
              class="text-none font-weight-bold"
            >
              <v-icon start size="18">{{ getCicloIcon(ciclo.deck_id) }}</v-icon>
              {{ ciclo.rotulo }} ({{ ciclo.n_cards }})
            </v-tab>
          </v-tabs>

          <!-- Iteração sobre cada ciclo selecionado -->
          <div v-for="ciclo in clinicosCiclos" :key="'content-' + ciclo.deck_id" v-show="activeCicloTab === ciclo.deck_id">
            <v-alert
              color="indigo"
              variant="tonal"
              class="mb-4 rounded-lg text-body-2"
              icon="mdi-account-group-outline"
            >
              {{ ciclo.descricao }}
            </v-alert>

            <!-- Lista de Temas do Ciclo -->
            <v-expansion-panels variant="accordion" class="vs-panels">
              <v-expansion-panel
                v-for="tema in ciclo.temas"
                :key="tema.deck_id"
                class="mb-2 rounded-lg elevation-1"
              >
                <v-expansion-panel-title class="py-3">
                  <div class="d-flex align-center justify-space-between w-100 pr-2">
                    <div class="d-flex align-center">
                      <span class="font-weight-bold text-subtitle-1" style="color: #1e293b;">
                        {{ tema.rotulo }}
                      </span>
                    </div>
                    <div class="d-flex align-center" @click.stop>
                      <v-chip size="x-small" color="primary" variant="outlined" class="mr-2 font-weight-bold">
                        {{ tema.n_cards }} cards
                      </v-chip>
                      <v-chip v-if="tema.peso_prova" size="x-small" color="amber-darken-3" variant="tonal" class="mr-3 font-weight-bold">
                        {{ tema.peso_prova }} questões
                      </v-chip>
                      <v-btn
                        icon
                        size="small"
                        color="primary"
                        elevation="1"
                        class="play-btn"
                        :disabled="isDemoRestricted(tema)"
                        @click.stop="startStudy(tema)"
                        title="Estudar este tema"
                      >
                        <v-icon size="18">mdi-play</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <v-list density="compact" class="py-0">
                    <v-list-item
                      v-for="subtema in tema.subtemas"
                      :key="subtema.deck_id"
                      class="py-2 px-3 mb-1 subtema-item rounded"
                    >
                      <template #prepend>
                        <v-icon size="16" color="#315fd8" class="mr-2">mdi-circle-small</v-icon>
                      </template>
                      <v-list-item-title class="text-body-2 font-weight-medium text-wrap" style="color: #334155;">
                        {{ subtema.rotulo }}
                      </v-list-item-title>
                      <template #append>
                        <div class="d-flex align-center">
                          <v-chip size="x-small" color="grey-darken-1" variant="tonal" class="mr-2">
                            {{ subtema.n_cards }} cards
                          </v-chip>
                          <v-chip v-if="subtema.peso_prova" size="x-small" color="amber-darken-3" variant="outlined" class="mr-3">
                            {{ subtema.peso_prova }} q
                          </v-chip>
                          <v-btn
                            icon
                            size="x-small"
                            color="primary"
                            variant="elevated"
                            class="play-sub-btn"
                            :disabled="isDemoRestricted(subtema)"
                            @click.stop="startStudy(subtema)"
                            title="Estudar este subtema"
                          >
                            <v-icon size="14">mdi-play</v-icon>
                          </v-btn>
                        </div>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import manifesto from '@/data/manifesto-decks.json';
import { isCardDue, getTodayDateString } from '@/utils/sm2';

export default {
  name: 'DecksV2',
  data() {
    return {
      activeBlockTab: 'conceitos',
      activeCicloTab: 'clinicos/pediatria',
      manifestoDecks: manifesto.decks || [],
    };
  },
  computed: {
    totalCardsCount() {
      return manifesto.n_cards_ativos || 1752;
    },
    // Registro de cards devidos para hoje segundo o SM-2
    dueCardsCount() {
      const userData = (this.$store.getters.userName && this.$store.getters.userName.length > 0)
        ? this.$store.getters.userName[0]
        : {};
      const trackingV2 = userData.difficultyTrackingV2 || [];
      const today = getTodayDateString();
      return trackingV2.filter(item => isCardDue(item, today)).length;
    },
    conceitosBlock() {
      return this.manifestoDecks.find(d => d.deck_id === 'conceitos');
    },
    conceitosTotalCards() {
      return this.conceitosBlock?.n_cards || 595;
    },
    clinicosBlock() {
      return this.manifestoDecks.find(d => d.deck_id === 'clinicos');
    },
    clinicosTotalCards() {
      return this.clinicosBlock?.n_cards || 1157;
    },
    // Bloco 1: Temas e subtemas de Conceitos Importantes
    conceitosTemas() {
      const temas = this.manifestoDecks.filter(d => d.parent_id === 'conceitos' && d.visivel);
      return temas.map(tema => {
        const subtemas = this.manifestoDecks.filter(d => d.parent_id === tema.deck_id && d.visivel);
        const deckIds = subtemas.map(s => s.deck_id);
        return {
          ...tema,
          subtemas,
          deckIds,
        };
      });
    },
    // Bloco 2: 4 Ciclos de Vida e seus respectivos temas e subtemas
    clinicosCiclos() {
      const ciclos = this.manifestoDecks.filter(d => d.parent_id === 'clinicos' && d.visivel);
      return ciclos.map(ciclo => {
        const temas = this.manifestoDecks.filter(d => d.parent_id === ciclo.deck_id && d.visivel);
        const mappedTemas = temas.map(tema => {
          const subtemas = this.manifestoDecks.filter(d => d.parent_id === tema.deck_id && d.visivel);
          const deckIds = subtemas.map(s => s.deck_id);
          return {
            ...tema,
            subtemas,
            deckIds,
          };
        });
        const allCicloDeckIds = mappedTemas.flatMap(t => t.deckIds);
        return {
          ...ciclo,
          temas: mappedTemas,
          deckIds: allCicloDeckIds,
        };
      });
    },
    isFreemium() {
      return this.$store.getters.freemium === 'true' || this.$store.getters.freemium === true;
    },
    userDemo() {
      const userName = this.$store.getters.userName;
      if (Array.isArray(userName) && userName.length > 0) {
        return userName[0].demo;
      }
      return '';
    },
  },
  methods: {
    getCicloIcon(deckId) {
      switch (deckId) {
        case 'clinicos/pediatria':
          return 'mdi-baby-carriage';
        case 'clinicos/go':
          return 'mdi-human-pregnant';
        case 'clinicos/adulto':
          return 'mdi-account';
        case 'clinicos/idoso':
          return 'mdi-human-cane';
        default:
          return 'mdi-folder';
      }
    },
    isDemoRestricted(item) {
      if (this.userDemo === 'yes') {
        // Na demonstração limitada, permite apenas o primeiro deck/tema
        return false;
      }
      return false;
    },
    startStudy(node) {
      // Se for subtema folha, passa deck_id direto. Se for tema ou ciclo, passa array de deckIds descendentes
      const payload = {
        deck_id: node.deck_id,
        deckIds: node.deckIds || [node.deck_id],
        deck_title: node.rotulo,
      };

      console.log('[Decks 2.0] Iniciando estudo do nó:', payload);

      this.$store.dispatch('generateCardsV2', payload)
        .then(() => {
          this.$router.push('/webapp/flashcardsv2');
        })
        .catch(err => {
          console.error('[Decks 2.0] Erro ao iniciar cards 2.0:', err);
        });
    },
    studyDueCards() {
      const payload = {
        deck_title: 'Revisão do Dia (SM-2)',
        onlyDue: true,
      };
      console.log('[Decks 2.0] Iniciando revisão de cards devidos do dia');
      this.$store.dispatch('generateCardsV2', payload)
        .then(() => {
          this.$router.push('/webapp/flashcardsv2');
        })
        .catch(err => {
          console.error('[Decks 2.0] Erro ao iniciar revisão de devidos:', err);
        });
    },
  },
};
</script>

<style scoped>
.decks-v2-container {
  max-width: 1000px;
  margin: 0 auto;
}

.header-badges-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.header-badge {
  height: 36px !important;
  display: inline-flex !important;
  align-items: center !important;
  border-radius: 9999px !important;
  font-size: 13px !important;
  line-height: 1 !important;
  margin: 0 !important;
}

.header-due-btn {
  height: 36px !important;
  display: inline-flex !important;
  align-items: center !important;
  border-radius: 9999px !important;
  font-size: 13px !important;
  line-height: 1 !important;
  padding: 0 16px !important;
  margin: 0 !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.header-due-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35) !important;
}

.vs-tabs {
  border-bottom: 1px solid #e2e8f0;
}

.vs-tabs :deep(.v-tab) {
  min-height: 48px !important;
  font-size: 15px !important;
  overflow: visible !important;
}

.vs-tabs :deep(.v-btn__content) {
  overflow: visible !important;
  line-height: 1.4 !important;
}

.ciclo-tabs {
  background: #f1f5f9;
  border-radius: 12px;
  padding: 6px 8px;
  min-height: 48px;
}

.ciclo-tabs :deep(.v-slide-group__container) {
  min-height: 42px;
  overflow: visible !important;
}

.ciclo-tabs :deep(.v-slide-group__content) {
  min-height: 42px;
  align-items: center;
  gap: 6px;
  overflow: visible !important;
}

.ciclo-tabs :deep(.v-tab) {
  min-height: 38px !important;
  height: 38px !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  text-transform: none !important;
  font-size: 14px !important;
  letter-spacing: 0 !important;
  color: #475569 !important;
  transition: all 0.2s ease !important;
  overflow: visible !important;
}

.ciclo-tabs :deep(.v-tab--selected) {
  background-color: #ffffff !important;
  color: #1e40af !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08) !important;
}

.ciclo-tabs :deep(.v-tab__slider) {
  display: none !important;
}

.ciclo-tabs :deep(.v-btn__content) {
  overflow: visible !important;
  line-height: 1.4 !important;
}

.vs-panels .v-expansion-panel {
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.vs-panels .v-expansion-panel:hover {
  border-color: #cbd5e1;
}

.subtema-item {
  background-color: #f8fafc;
  transition: background-color 0.2s ease;
}

.subtema-item:hover {
  background-color: #f1f5f9;
}

.play-btn {
  background-color: #315fd8 !important;
  color: white !important;
}

.play-sub-btn {
  background-color: #315fd8 !important;
  color: white !important;
}
</style>
